// frontend/src/components/layout/useStoreSwitch.jsx
//
// Switching the session to another store, for every surface that offers it:
// the desktop user dropdown and the phone's store sheet. When a merchant may
// switch, the "your tabs will close" gate, the teardown handed to
// AuthContext.switchStore and the failure message all live here, so the two
// surfaces only decide how the stores look.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTabs } from '../../tabs/useTabs';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { translateRequestError } from '../../queries/api';
import ConfirmDialog from '../common/ConfirmDialog';

/**
 * @returns {{
 *   canSwitch: boolean,           the merchant has another store to go to
 *   switchTo: (storeId) => Promise<void>,
 *   switchingId: string | null,   the store being switched to; stays set on
 *                                 success, because the page is on its way out
 *   error: string | null,         why the last switch failed, ready to show
 *   confirmDialog: JSX.Element,   render it once, at a stable position
 * }}
 */
export function useStoreSwitch() {
  const { t } = useTranslation();
  const { stores, switchStore } = useAuth();
  const { countOpen, closeAllExceptHome } = useTabs();
  const { confirm, dialogProps } = useConfirmDialog();
  const [switchingId, setSwitchingId] = useState(null);
  const [error, setError] = useState(null);

  const switchTo = async (storeId) => {
    // The restart after a successful switch wipes the non-Home tabs anyway;
    // the gate lets the merchant keep work in progress by not switching.
    // ConfirmDialog defaults the button labels to common.confirm / cancel.
    if (countOpen > 1) {
      const ok = await confirm({
        title: t('tabs.storeSwitchTitle'),
        message: t('tabs.storeSwitchWarning'),
        variant: 'warning',
      });
      if (!ok) return;
    }

    setSwitchingId(storeId);
    setError(null);
    try {
      // switchStore replaces the document onto the new store; the callback is
      // its "selection committed, teardown now" seam, so the non-Home tabs are
      // closed only on success — a failed switch preserves them.
      await switchStore(storeId, closeAllExceptHome);
    } catch (err) {
      console.error('Failed to switch store:', err.message);
      setError(translateRequestError(err, t, 'storeSwitcher.switchFailed'));
      setSwitchingId(null);
    }
  };

  return {
    canSwitch: stores.length > 1,
    switchTo,
    switchingId,
    error,
    confirmDialog: <ConfirmDialog {...dialogProps} />,
  };
}
